import api from './api';
import type { Inspection } from '../types';

export const inspectionService = {
  async getAll(filters?: any): Promise<Inspection[]> {
    const response = await api.get('/inspections', { params: filters });
    return response.data.inspections;
  },

  async getById(id: number): Promise<Inspection> {
    const response = await api.get(`/inspections/${id}`);
    return response.data;
  },

  async getByProjectId(projectId: string): Promise<Inspection[]> {
    const response = await api.get(`/inspections/project/${projectId}`);
    return response.data.buildings;
  },

  async create(data: any): Promise<Inspection> {
    const response = await api.post('/inspections', data);
    return response.data.inspection;
  },

  async update(id: number, data: any): Promise<{
    inspection: Inspection;
    insightlyResult?: { updated: boolean; message: string; projectId?: string; dateRange?: string };
    driveResult?: { success: boolean; message: string };
  }> {
    const response = await api.patch(`/inspections/${id}`, data);
    return {
      inspection: response.data.inspection,
      insightlyResult: response.data.insightlyResult,
      driveResult: response.data.driveResult
    };
  },

  // Returns full response including insightlyError
  async updateRaw(id: number, data: any): Promise<{
    inspection: Inspection;
    insightlyError?: { projectId: string; error: string };
  }> {
    const response = await api.patch(`/inspections/${id}`, data);
    return response.data;
  },

  async batchUpdate(data: {
    inspectionIds: number[];
    commonUpdates: any;
    perBuildingOverrides?: any[];
  }): Promise<{
    updated: number;
    insightlyErrors?: Array<{ inspectionId: number; projectId: string; error: string }>;
    driveResults?: Array<{ inspectionId: number; projectId: string; success: boolean; message: string }>;
  }> {
    const response = await api.patch('/inspections/batch', data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/inspections/${id}`);
  },

  async batchDelete(inspectionIds: number[]): Promise<{ deletedCount: number }> {
    const response = await api.delete('/inspections/batch', { data: { inspectionIds } });
    return response.data;
  },

  async deleteByProjectId(projectId: string): Promise<{ deletedCount: number; projectId: string }> {
    const response = await api.delete(`/inspections/by-project/${projectId}`);
    return response.data;
  },

  async uploadExcel(rows: Array<{
    projectId: string;
    projectName: string;
    siteName: string;
    address: string;
    inspectionDate: string;
    inspectionTime: string;
    coordinationStatus: string;
    assignedInspector: string;
    salesRep: string;
  }>, inspectorOverride?: string): Promise<{
    created: number;
    updated: number;
    errors: Array<{ row: number; error: string }>;
  }> {
    const response = await api.post('/inspections/upload-excel', { rows, inspectorOverride });
    return response.data;
  },

  async getInspectors(): Promise<Array<{ id: number; name: string; email?: string }>> {
    const response = await api.get('/inspectors');
    return response.data.inspectors || [];
  },

  async uploadPlanningRoute(rows: Array<{
    inspector: string;
    date: string;
    time: string;
    publicAgency: string;
    siteName: string;
    address: string;
    notes: string;
  }>, dryRun: boolean = false): Promise<{
    matched: Array<{ address: string; inspectionIds: number[]; updatedCount: number }>;
    unmatched: string[];
    totalUpdated: number;
  }> {
    const response = await api.post('/inspections/upload-planning-route', { rows, dryRun });
    return response.data;
  },
};
